import { useEffect, useRef } from "react";
import { gsap } from "@/animations/gsap";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useIsDesktop } from "@/hooks/useDevice";

const LINES = [
  "It is architecture.",
  "It is reliability.",
  "It is maintainability.",
  "It is observability.",
  "It is usability.",
  "It is knowing what NOT to build.",
];

export function Philosophy() {
  const ref = useRef<HTMLElement>(null);
  const reduced = usePrefersReducedMotion();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (reduced) return undefined;
    const ctx = gsap.context(() => {
      if (isDesktop) {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: ref.current,
            start: "top top",
            end: "+=170%",
            pin: true,
            scrub: 0.5,
            anticipatePin: 1,
          },
        });
        tl.fromTo(".phil-line-inner", { yPercent: 115 }, { yPercent: 0, stagger: 0.22, duration: 1.4, ease: "power3.out" })
          .fromTo(".phil-item", { opacity: 0.12, y: 26 }, { opacity: 1, y: 0, stagger: 1.1, duration: 2, ease: "power2.out" }, ">")
          .to({}, { duration: 0.8 });
      } else {
        gsap.fromTo(
          ".phil-line-inner",
          { yPercent: 115 },
          { yPercent: 0, stagger: 0.1, duration: 0.9, ease: "power3.out", scrollTrigger: { trigger: ref.current, start: "top 78%" } }
        );
        gsap.utils.toArray<HTMLElement>(".phil-item").forEach((el) => {
          gsap.fromTo(el, { opacity: 0.12, y: 22 }, { opacity: 1, y: 0, duration: 0.7, scrollTrigger: { trigger: el, start: "top 88%" } });
        });
      }
    }, ref);
    return () => ctx.revert();
  }, [reduced, isDesktop]);

  return (
    <section id="philosophy" ref={ref} className="section-y" data-testid="philosophy-section">
      <div className="container-x">
        <p className="eyebrow">03 — PHILOSOPHY</p>
        <h2 className="font-display mt-8 text-[clamp(2.5rem,6vw,5.5rem)] font-semibold uppercase leading-[0.98] tracking-tight">
          <span className="mask-line">
            <span className="phil-line-inner">GOOD SOFTWARE</span>
          </span>
          <span className="mask-line">
            <span className="phil-line-inner text-outline">IS NOT JUST CODE.</span>
          </span>
        </h2>
        <ul className="mt-14 grid gap-4 md:gap-6">
          {LINES.map((line) => (
            <li key={line} className="phil-item font-display text-xl font-medium tracking-tight md:text-3xl" data-testid="philosophy-line">
              {line}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
